import {
  faDotCircle,
  faExclamationTriangle,
  faPlusSquare,
  faQuestionSquare
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Button,
  Grid,
  ListItemText,
  Paper,
  Tooltip
} from '@mui/material';
import { StyledListItem } from 'components';
import { Text } from 'components/_commons';
import { PrimaryButton } from 'components/_commons/GenericTable/GenericTable';
import debounce from 'debounce-promise';
import { useModal, useStores } from 'hooks';
import { runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useState } from 'react';
import AsyncSelect from 'react-select/async';
import { QuestionService } from 'services';
import { translate } from 'utils';
import { CustomListItem } from './CustomListItem';

export const QuestionManualSelect = observer(({ formData, componentLocation }) => {
  const { index } = formData;

  const { enqueueSnackbar } = useSnackbar();
  const displayModal = useModal();

  const { quizStore, questionStore } = useStores();

  const { quiz } = quizStore;

  const [questions, setQuestions] = useState(() => formData.questions || []);

  const [selectedQuestions, setSelectedQuestions] = useState([]);
  const [error, setError] = useState('');

  useEffect(() => {
  }, []);

  const checkFormValidity = useCallback(() => {
    if (questions.length === 0) {
      quizStore.setIsQuizFormPageValid(false);
      return setError(translate('quiz.noQuestionsInPage'));
    }
    quizStore.setIsQuizFormPageValid(true);
    return setError('');
  }, [quizStore, questions]);

  useEffect(() => {
    checkFormValidity();
  }, [checkFormValidity]);

  const updatePage = useCallback((key, data) => {
    if (Object.keys(formData).length > 0) {
      runInAction(() => { quiz.pages[index][key] = data[key]; });
    } else {
      quizStore.updatePage({ isRandom: false, ...data })
      && quizStore.updateContentInCurrentPage(index);
    }
  }, [formData, quizStore, quiz, index]);

  const SelectWrapper = React.forwardRef((props, ref) => (
    <div ref={ref} style={{ width: '100%' }}>
      <AsyncSelect {...props} />
    </div>
  ));

  const handleAddQuestion = useCallback(value => {
    if (!value) {
      enqueueSnackbar(translate('errors.invalidQuestionData') || 'Invalid question data', { variant: 'error' });
      return;
    }

    if (!value.id) {
      enqueueSnackbar('Question ID is missing', { variant: 'error' });
      return;
    }

    if (!value.title) {
      value.title = translate('common.untitledQuestion') || 'Question sans titre';
    }

    const newQuestions = [...questions];
    newQuestions.push(value);
    setQuestions(newQuestions);

    const newQuestionsTmp = [...selectedQuestions];
    newQuestionsTmp.push(value);
    setSelectedQuestions(newQuestionsTmp);

    updatePage('questions', { questions: newQuestions });
  }, [questions, selectedQuestions, updatePage, enqueueSnackbar]);

  const handleFilterOptions = useCallback(opt => {
    const result = !questions.find(q => q.id === opt.data.id);
    return result;
  }, [questions]);

  const createQuestion = useCallback((ques, onClose) => {
    questionStore.createUpdateQuestion(ques.id, ques)
        .then(response => {
          QuestionService.getQuestion(response)
              .then(questionResponse => {
                // Ensure questionResponse has a title before adding
                if (!questionResponse.title) {
                  questionResponse.title = translate('common.untitledQuestion') || 'Question sans titre';
                }

                handleAddQuestion(questionResponse);
                onClose();
                enqueueSnackbar(translate('confirms.questionList.createdAndOnList'), { variant: 'success' });
              })
              .catch(err => {
                enqueueSnackbar(err.message, { variant: 'error' });
              });
        })
        .catch(err => {
          enqueueSnackbar(err.message, { variant: 'error' });
        });
  }, [enqueueSnackbar, questionStore, handleAddQuestion]);

  const handleCreateQuestion = useCallback(
      () => {
        displayModal({
          type: 'CREATE_QUESTION',
          onConfirm: createQuestion,
          defaultValues: {}
        });
      },
      [displayModal, createQuestion]
  );

  const getAsyncOptions = useCallback(inputValue => new Promise(resolve => {
      QuestionService
          .getQuestionListForOptions({ freeSearch: inputValue }, 0)
          .then(response => {
            // Map response to ensure all required fields are present
            const formattedResponse = response.content.map(item => ({
                ...item,
                id: item.id || Math.random().toString(36).substr(2, 9),
                title: item.title || translate('common.untitledQuestion') || 'Question sans titre'
              }));

            return resolve(formattedResponse);
          })
          .catch(error => {
            enqueueSnackbar(translate('errors.failedToLoadQuestions') || 'Failed to load questions', { variant: 'error' });
            return resolve([]);
          });
    }), [enqueueSnackbar]);

  const debouncedLoadOptions = debounce(getAsyncOptions, 500);

  const handleKeyDown = e => {
    if (e.key === 'Enter' || e.key === 'Tab') {
      return e.preventDefault();
    }
    return null;
  };

  const handleMoveQuestionUp = useCallback((pageIndex, questionId) => {
    setQuestions(formData.questions);
    quizStore.moveQuestionUp(pageIndex, questionId);
  }, [quizStore, formData]);

  const handleMoveQuestionDown = useCallback((pageIndex, questionId) => {
    setQuestions(formData.questions);
    quizStore.moveQuestionDown(pageIndex, questionId);
  }, [quizStore, formData]);

  const handleDeleteQuestion = useCallback(questionId => {
    quizStore.toggleDeletingQuestion(true);

    const newQuestionsList = [...questions].filter(ques => ques.id !== questionId);

    setQuestions(newQuestionsList);
    setSelectedQuestions(newQuestionsList);

    updatePage('questions', { questions: newQuestionsList });
  }, [questions, quizStore, updatePage]);

  const createAndAddNewQuestionInCurrentPage = () => (
    <Paper style={{ padding: 10 }} variant="outlined">
      <Grid alignItems="center" container direction="column" justifyContent="center">
        <Grid item>
          <PrimaryButton onClick={handleCreateQuestion}>
            <FontAwesomeIcon icon={faPlusSquare} size="lg" />
          </PrimaryButton>
        </Grid>
        <Grid item>
          <Text fontSize="14px">{translate('quiz.addQuestionInCurrentPage')}</Text>
        </Grid>
      </Grid>
    </Paper>
  );

  const CustomOptionQuestion = ({ data, innerProps }) => (
    <StyledListItem {...innerProps}>
      <ListItemText primary={data.title || translate('common.untitledQuestion') || 'Question sans titre'} />
    </StyledListItem>
    );

  // Ensure questions are properly loaded from formData when it changes
  useEffect(() => {
    if (formData.questions && formData.questions.length > 0) {
      const processedQuestions = formData.questions.map(q => {
        if (!q.title) {
          return {
            ...q,
            title: translate('common.untitledQuestion') || 'Question sans titre'
          };
        }
        return q;
      });

      setQuestions(processedQuestions);
    }
  }, [formData.questions]);

  return (
    <Grid alignItems="center" container direction="row" justifyContent="center">
      <Grid container direction="column" justifyContent="flex-start" wrap="nowrap">

        {error && (
          <Grid alignItems="center" container>
            <Grid item xs={11}>
              <Text color="var(--error-color)">
                <FontAwesomeIcon icon={faExclamationTriangle} size="lg" style={{ color: 'var(--error-color)' }} />
                {' '}
                {error}
              </Text>
            </Grid>
          </Grid>
          )}

        <Grid item>
          <Text fontWeight="bold">{translate('quiz.searchQuestions')}</Text>
        </Grid>

        <Grid item style={{ width: '70%', marginBottom: 20 }}>
          <Tooltip placement="top" title={translate('quiz.tooltip.questionSearchToolTip')}>
            <div style={{ width: '100%' }}>
              <AsyncSelect
                    cacheOptions
                    closeMenuOnSelect
                    components={{ Option: CustomOptionQuestion }}
                    defaultOptions
                    filterOption={handleFilterOptions}
                    getOptionLabel={(option) => option.title || translate('common.untitledQuestion') || 'Question sans titre'}
                    getOptionValue={(option) => option.id}
                    loadingMessage={() => translate('common.loading')}
                    loadOptions={inputValue => debouncedLoadOptions(inputValue)}
                    name="questions"
                    noOptionsMessage={() => translate('errors.noOptions')}
                    placeholder={translate('quiz.addQuestions')}
                    styles={{ menu: base => ({ ...base, zIndex: 2000 }) }}
                    onChange={handleAddQuestion}
                    onKeyDown={handleKeyDown}
                />
            </div>
          </Tooltip>
        </Grid>
      </Grid>

      {componentLocation === 'modal' && (
        <Grid container direction="column" justifyContent="flex-start" wrap="nowrap">
          <Grid item xs={4}>
            <Text fontWeight="bold">{translate('quiz.questionNotExists')}</Text>
          </Grid>
          <Grid item style={{ maxWidth: 205, marginBottom: 15 }}>
            <Button
                    color="primary"
                    fullWidth
                    startIcon={<FontAwesomeIcon icon={faQuestionSquare} />}
                    variant="contained"
                    onClick={handleCreateQuestion}
                >
              {translate('button.createQuestion')}
            </Button>
          </Grid>
        </Grid>
        )}

      {questions && questions.length > 0 && (
        <Grid container justifyContent="flex-start">
          <Text fontWeight="bold" textTransform="uppercase">{translate('pageQuizList.selectedQuestions')}</Text>

          {questions.map((question, idx) => {
                // Ensure question title is defined
                if (!question.title) {
                  question.title = translate('common.untitledQuestion') || 'Question sans titre';
                }

                return (
                  <CustomListItem
                        isMovableDown={componentLocation !== 'modal'}
                        isMovableUp={componentLocation !== 'modal'}
                        key={question.id || `question-${idx}`}
                        startIcon={<FontAwesomeIcon icon={faDotCircle} size="xs" />}
                        title={question.title}
                        onDelete={() => handleDeleteQuestion(question.id)}
                        onMoveItemDown={() => handleMoveQuestionDown(index, question.id)}
                        onMoveItemUp={() => handleMoveQuestionUp(index, question.id)}
                    />
                );
              })}
        </Grid>
        )}

      {componentLocation !== 'modal' && (
        <Grid item style={{ marginTop: 20 }}>
          {createAndAddNewQuestionInCurrentPage()}
        </Grid>
        )}
    </Grid>
  );
});

QuestionManualSelect.propTypes = {
  formData: PropTypes.shape({}),
  componentLocation: PropTypes.string.isRequired
};

QuestionManualSelect.defaultProps = {
  formData: {}
};
